import { API_BASE } from '@/shared/services/api';
import { useState, useEffect, useCallback } from 'react';

const API_URL = API_BASE || '';

export const useFleetAPI = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAPI = useCallback(async (endpoint, options = {}) => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('ezerpToken');
    try {
      const response = await fetch(`${API_URL}/api/fleet${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
          ...options.headers
        },
        ...options
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { fetchAPI, loading, error };
};

export const useFleetDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const { fetchAPI } = useFleetAPI();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAPI('/dashboard');
      setDashboard(data);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchAPI]);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [refresh]);

  return { dashboard, loading, refresh };
};

export const useVehicles = (filters = {}) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { fetchAPI } = useFleetAPI();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value);
        }
      });
      const data = await fetchAPI(`/vehicles?${params.toString()}`);
      setVehicles(data);
    } catch (err) {
      console.error('Failed to fetch vehicles:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchAPI, JSON.stringify(filters)]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { vehicles, loading, refresh };
};

export const useLiveVehicles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { fetchAPI } = useFleetAPI();

  const refresh = useCallback(async () => {
    try {
      const data = await fetchAPI('/vehicles/live');
      setVehicles(data);
    } catch (err) {
      console.error('Failed to fetch live vehicles:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchAPI]);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, [refresh]);

  return { vehicles, loading, refresh };
};

export const useDrivers = (filters = {}) => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { fetchAPI } = useFleetAPI();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value);
        }
      });
      const data = await fetchAPI(`/drivers?${params.toString()}`);
      setDrivers(data);
    } catch (err) {
      console.error('Failed to fetch drivers:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchAPI, JSON.stringify(filters)]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { drivers, loading, refresh };
};

export const useTrips = (filters = {}) => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const { fetchAPI } = useFleetAPI();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value);
        }
      });
      const data = await fetchAPI(`/trips?${params.toString()}`);
      setTrips(data);
    } catch (err) {
      console.error('Failed to fetch trips:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchAPI, JSON.stringify(filters)]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { trips, loading, refresh };
};

export const useAlerts = (filters = {}) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { fetchAPI } = useFleetAPI();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value);
        }
      });
      const data = await fetchAPI(`/alerts?${params.toString()}`);
      setAlerts(data);
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchAPI, JSON.stringify(filters)]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { alerts, loading, refresh };
};

export const useVehicleTypes = () => {
  const [types, setTypes] = useState([]);
  const { fetchAPI } = useFleetAPI();

  useEffect(() => {
    const fetch = async () => {
      try {
        const data = await fetchAPI('/vehicles/types');
        setTypes(data);
      } catch (err) {
        console.error('Failed to fetch vehicle types:', err);
      }
    };
    fetch();
  }, [fetchAPI]);

  return types;
};

export const useAnalytics = (period = '30d') => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const { fetchAPI } = useFleetAPI();

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const data = await fetchAPI(`/analytics/summary?period=${period}`);
        setAnalytics(data);
      } catch (err) {
        console.error('Failed to fetch analytics:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [fetchAPI, period]);

  return { analytics, loading };
};
